import './styles.scss';
import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {
  Card,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Notify, Loading} from 'notiflix';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import DataTable from 'examples/Tables/DataTable';
import MDButton from 'components/MDButton';
import {Axios} from 'services/axios';

const AdditionalFeatureAssignment = ({
  setSelectedAdditionalFeatures = () => {},
  selectedAdditionalFeatures = [],
  initialSelectedAdditionalFeatureIds = [],
  customSelectedAdditionalFeatures = [],
  setCustomSelectedAdditionalFeatures,
}) => {
  // ------------------ Additional Features State ------------------
  const [additionalFeaturesData, setAdditionalFeaturesData] = useState([]);
  const [flattenedAdditionalFeatures, setFlattenedAdditionalFeatures] =
    useState([]);
  const [uniqueAdditionalSubCategories, setUniqueAdditionalSubCategories] =
    useState([]);
  const [additionalFilterSubCategory, setAdditionalFilterSubCategory] =
    useState('all');
  const [additionalSearchQuery, setAdditionalSearchQuery] = useState('');
  const [sortByAdditionalFeature, setSortByAdditionalFeature] =
    useState('recentlyAdded');
  const [openEditAdditionalFeatureDialog, setOpenEditAdditionalFeatureDialog] =
    useState(false);
  const [isAdditionalTableExpanded, setIsAdditionalTableExpanded] =
    useState(false);
  const [editAdditionalFeatureForm, setEditAdditionalFeatureForm] = useState({
    id: '',
    subCategoryId: '',
    subCategoryName: '',
    name: '',
    description: '',
    featureDefaultPrice: '',
  });

  // ------------------ Fetch Additional Features ------------------
  const fetchAdditionalFeatures = useCallback(async () => {
    try {
      Loading.circle('Fetching additional features');
      const response = await Axios.fetch('/shop/additional-features');
      setAdditionalFeaturesData(response.data.data || []);
    } catch (err) {
      Notify.failure(
        err.response?.data?.message || 'Failed to fetch additional features',
      );
      console.error(err);
    } finally {
      Loading.remove('Fetching additional features');
    }
  }, []);

  useEffect(() => {
    fetchAdditionalFeatures();
  }, [fetchAdditionalFeatures]);

  // ------------------ Flatten Additional Features ------------------
  // IMPORTANT: Remove selectedAdditionalFeatures from dependencies so that user toggles are preserved.
  useEffect(() => {
    const flattened = [];
    const uniques = [];
    additionalFeaturesData.forEach(sub => {
      uniques.push({id: sub.id, subCategoryName: sub.subCategoryName});
      if (sub.features && Array.isArray(sub.features)) {
        sub.features.forEach(feature => {
          // Only use the initial selection (do not override user toggles)
          const isAlreadySelected =
            initialSelectedAdditionalFeatureIds.includes(feature.id);
          flattened.push({
            ...feature,
            subCategoryId: sub.id,
            subCategoryName: sub.subCategoryName,
            isSelected: isAlreadySelected,
            // Initialize price fields as empty strings; these can be modified by the user.
            lowPrice: '',
            midPrice: '',
            highPrice: '',
          });
        });
      }
    });
    // setFlattenedAdditionalFeatures(flattened);
    // console.log('flattenedAdditionalFeatures', flattened);
    const alreadySelectedFeatures = transformCustomSelectedFeatures();
    // console.log('flattenedAdditionalFeatures extraFlattened', extraFlattened);
    const replacedArray = flattened.map(item => {
      // console.log('ids item.id', item.id);
      // console.log(
      //   'custom ids',
      //   alreadySelectedFeatures.map(u => u.id),
      // );

      const updated = alreadySelectedFeatures.find(u => u.id === item.id);
      return updated ? {...item, ...updated} : item;
    });
    // console.log('flattenedAdditionalFeatures  replacedArra', replacedArray);
    setFlattenedAdditionalFeatures(replacedArray);
    setUniqueAdditionalSubCategories(uniques);
  }, [additionalFeaturesData, initialSelectedAdditionalFeatureIds]);

  const transformCustomSelectedFeatures = () => {
    const transformed = customSelectedAdditionalFeatures.map(item => {
      const feature = item.additionalFeature;
      console.log('ujjwal', item);
      const isAlreadySelected = initialSelectedAdditionalFeatureIds.includes(
        feature.id,
      );
      // delete feature.subCategory;
      return {
        additionalFeatureId: item.id,
        subCategoryName: feature?.subCategory?.subCategoryName || '',
        isSelected: isAlreadySelected,
        lowPrice: item.lowPrice || '',
        midPrice: item.midPrice || '',
        highPrice: item.highPrice || '',
        ...feature,
      };
    });
    console.log(
      'transfo customSelectedAdditionalFeatures',
      initialSelectedAdditionalFeatureIds,
    );
    console.log('features transformed', transformed);
    return transformed;
  };

  // ------------------ Filter and Sort Additional Features ------------------
  const filteredAdditionalFeatures = useMemo(() => {
    let features = flattenedAdditionalFeatures;
    if (additionalFilterSubCategory !== 'all') {
      features = features.filter(
        f => f.subCategoryId === additionalFilterSubCategory,
      );
    }
    if (additionalSearchQuery.trim() !== '') {
      features = features.filter(f =>
        f.name.toLowerCase().includes(additionalSearchQuery.toLowerCase()),
      );
    }
    if (sortByAdditionalFeature === 'recentlyAdded') {
      features = features
        .slice()
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortByAdditionalFeature === 'recentlyUpdated') {
      features = features
        .slice()
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else if (sortByAdditionalFeature === 'alphabeticallyAsc') {
      features = features.slice().sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortByAdditionalFeature === 'alphabeticallyDesc') {
      features = features.slice().sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortByAdditionalFeature === 'defaultPriceAsc') {
      features = features
        .slice()
        .sort(
          (a, b) =>
            parseFloat(a.featureDefaultPrice) -
            parseFloat(b.featureDefaultPrice),
        );
    } else if (sortByAdditionalFeature === 'defaultPriceDsc') {
      features = features
        .slice()
        .sort(
          (a, b) =>
            parseFloat(b.featureDefaultPrice) -
            parseFloat(a.featureDefaultPrice),
        );
    }

    // console.log('features original', features);
    // const transformred = transformCustomSelectedFeatures();

    // const replacedArray = features.map(item => {
    //   console.log('ids item.id', item.id);
    //   console.log(
    //     'custom ids',
    //     transformred.map(u => u.id),
    //   );

    //   const updated = transformred.find(u => u.id === item.id);
    //   return updated ? {...item, ...updated} : item;
    // });
    // console.log('features replacedArra', replacedArray);

    return features;
  }, [
    flattenedAdditionalFeatures,
    additionalFilterSubCategory,
    additionalSearchQuery,
    sortByAdditionalFeature,
  ]);

  console.log(
    'customSelectedAdditionalFeatures',
    customSelectedAdditionalFeatures,
  );

  // useEffect(() => {
  //   const transformed = customSelectedAdditionalFeatures.map(item => {
  //     const feature = item.additionalFeature;
  //     console.log('ujjwal', feature, item);
  //     return {
  //       ...feature,
  //       additionalFeatureId: item.id,
  //       subCategoryName: item?.subCategory?.subCategoryName || '',
  //       isSelected: false,
  //       lowPrice: item.lowPrice || '',
  //       midPrice: item.midPrice || '',
  //       highPrice: item.highPrice || '',
  //     };
  //   });
  //   console.log(
  //     'features transformed customSelectedAdditionalFeatures',
  //     customSelectedAdditionalFeatures,
  //   );
  //   console.log('features transformed', transformed);
  //   // setCustomSelectedAdditionalFeatures(transformed);
  // }, [
  //   selectedAdditionalFeatures,
  //   initialSelectedAdditionalFeatureIds,
  //   filteredAdditionalFeatures,
  // ]);

  // ------------------ Compute Selected Additional Features ------------------
  const computedSelectedFeatures = useMemo(() => {
    return flattenedAdditionalFeatures
      .filter(feature => feature.isSelected && feature.name)
      .map(({id, lowPrice, midPrice, highPrice, featureDefaultPrice}) => ({
        additionalFeatureId: id,
        lowPrice: lowPrice || featureDefaultPrice,
        midPrice: midPrice || featureDefaultPrice,
        highPrice: highPrice || featureDefaultPrice,
      }));
  }, [flattenedAdditionalFeatures]);

  // Update parent's selection when computed changes
  useEffect(() => {
    setSelectedAdditionalFeatures(computedSelectedFeatures);
  }, [computedSelectedFeatures, setSelectedAdditionalFeatures]);

  // ------------------ Handlers ------------------
  const handleCheckboxChange = featureId => {
    setFlattenedAdditionalFeatures(prev =>
      prev.map(feature =>
        feature.id === featureId
          ? {...feature, isSelected: !feature.isSelected}
          : feature,
      ),
    );
  };

  const handleAdditionalCustomPriceChange = (featureId, priceType, value) => {
    const key = priceType.toLowerCase() + 'Price';
    setFlattenedAdditionalFeatures(prev =>
      prev.map(f => (f.id === featureId ? {...f, [key]: value} : f)),
    );
  };

  // ------------------ Edit Additional Feature (Modal) ------------------
  const handleEditAdditionalFeature = id => {
    const feat = flattenedAdditionalFeatures.find(f => f.id === id);
    if (feat) {
      setEditAdditionalFeatureForm({
        id: feat.id,
        subCategoryId: feat.subCategoryId,
        subCategoryName: feat.subCategoryName,
        name: feat.name,
        description: feat.description,
        featureDefaultPrice: feat.featureDefaultPrice,
      });
      setOpenEditAdditionalFeatureDialog(true);
    }
  };

  const handleAdditionalEditChange = e => {
    const {name, value, type, checked} = e.target;
    setEditAdditionalFeatureForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // ------------------ Build DataTable Rows ------------------
  const getRow = (feature, index) => ({
    sr: index + 1,
    name: (
      <MDTypography
        variant="caption"
        onClick={() => handleEditAdditionalFeature(feature.id)}
        style={{cursor: 'pointer'}}>
        {feature.name}
      </MDTypography>
    ),
    description: (
      <MDTypography variant="caption">{feature.description}</MDTypography>
    ),
    subCategoryName: (
      <MDTypography variant="caption">{feature.subCategoryName}</MDTypography>
    ),
    featureDefaultPrice: (
      <MDTypography variant="caption">
        ${feature.featureDefaultPrice}
      </MDTypography>
    ),
    isSelected: (
      <Checkbox
        checked={feature.isSelected}
        onChange={() => handleCheckboxChange(feature.id)}
      />
    ),
    lowPrice: (
      <MDInput
        type="number"
        step="0.01"
        min="0"
        value={feature.lowPrice || feature.featureDefaultPrice || ''}
        onChange={e =>
          handleAdditionalCustomPriceChange(feature.id, 'Low', e.target.value)
        }
        sx={{width: '90px'}}
        onKeyDown={e => {
          if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
        }}
      />
    ),
    midPrice: (
      <MDInput
        type="number"
        step="0.01"
        min="0"
        value={feature.midPrice || feature.featureDefaultPrice}
        onChange={e =>
          handleAdditionalCustomPriceChange(feature.id, 'Mid', e.target.value)
        }
        sx={{width: '90px'}}
        onKeyDown={e => {
          if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
        }}
      />
    ),
    highPrice: (
      <MDInput
        type="number"
        step="0.01"
        min="0"
        value={feature.highPrice || feature.featureDefaultPrice}
        onChange={e =>
          handleAdditionalCustomPriceChange(feature.id, 'High', e.target.value)
        }
        sx={{width: '90px'}}
        onKeyDown={e => {
          if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
        }}
      />
    ),
  });

  const additionalColumns = [
    {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
    {Header: 'Feature Name', accessor: 'name', width: '20%', align: 'left'},
    {
      Header: 'Description',
      accessor: 'description',
      width: '30%',
      align: 'left',
    },
    {Header: 'SubCategory', accessor: 'subCategoryName', align: 'left'},
    {
      Header: 'Default Price',
      accessor: 'featureDefaultPrice',
      width: '10%',
      align: 'center',
    },
    {Header: 'Low Price', accessor: 'lowPrice', width: '10%', align: 'center'},
    {Header: 'Mid Price', accessor: 'midPrice', width: '10%', align: 'center'},
    {
      Header: 'High Price',
      accessor: 'highPrice',
      width: '10%',
      align: 'center',
    },
    {Header: 'Select', accessor: 'isSelected', align: 'center'},
  ];

  const additionalRows = filteredAdditionalFeatures.map((feature, idx) =>
    getRow(feature, idx),
  );

  return (
    <MDBox sx={{mt: 7}} className="additionalFeatureSection assignFeaturePage">
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card sx={{pb: 2}}>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              onClick={() => setIsAdditionalTableExpanded(prev => !prev)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
              }}>
              <MDTypography variant="h6" color="white">
                Additional Feature Assignment
              </MDTypography>
              <MDBox
                className="additionalFeatureTableSection"
                display="flex"
                alignItems="center"
                onClick={e => e.stopPropagation()}>
                <MDInput
                  placeholder="Search by additional feature name"
                  value={additionalSearchQuery}
                  sx={{width: 250, marginRight: '12px'}}
                  onChange={e => setAdditionalSearchQuery(e.target.value)}
                  size="small"
                />
                <Select
                  value={sortByAdditionalFeature}
                  onChange={e => setSortByAdditionalFeature(e.target.value)}
                  displayEmpty
                  sx={{height: 38, width: 180, color: 'white'}}
                  inputProps={{'aria-label': 'Sort By'}}>
                  <MenuItem value="alphabeticallyAsc">
                    Alphabetically (ASC)
                  </MenuItem>
                  <MenuItem value="alphabeticallyDesc">
                    Alphabetically (DSC)
                  </MenuItem>
                  <MenuItem value="defaultPriceAsc">
                    Default Price (ASC)
                  </MenuItem>
                  <MenuItem value="defaultPriceDsc">
                    Default Price (DSC)
                  </MenuItem>
                  <MenuItem value="recentlyAdded">Recently Added</MenuItem>
                  <MenuItem value="recentlyUpdated">Recently Updated</MenuItem>
                </Select>
                <IconButton
                  size="small"
                  aria-label={
                    isAdditionalTableExpanded
                      ? 'Collapse table'
                      : 'Expand table'
                  }
                  onClick={() => setIsAdditionalTableExpanded(prev => !prev)}
                  sx={{
                    color: 'white',
                    ml: 1,
                    transform: isAdditionalTableExpanded
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}>
                  <ExpandMoreIcon />
                </IconButton>
              </MDBox>
            </MDBox>
            {isAdditionalTableExpanded && (
              <>
                <MDBox mt={3} mb={2} sx={{px: 2}}>
                  <InputLabel id="filter-additional-label">
                    Filter by SubCategory
                  </InputLabel>
                  <Select
                    labelId="filter-additional-label"
                    value={additionalFilterSubCategory}
                    onChange={e =>
                      setAdditionalFilterSubCategory(e.target.value)
                    }
                    sx={{width: 300, height: '42px', mt: 1}}>
                    <MenuItem value="all">All Subcategories</MenuItem>
                    {uniqueAdditionalSubCategories.map(sub => (
                      <MenuItem key={sub.id} value={sub.id}>
                        {sub.subCategoryName}
                      </MenuItem>
                    ))}
                  </Select>
                </MDBox>
                <MDBox pt={1}>
                  <DataTable
                    table={{columns: additionalColumns, rows: additionalRows}}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    hoverEffect
                  />
                </MDBox>
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Edit Additional Feature Modal */}
      <Dialog
        open={openEditAdditionalFeatureDialog}
        onClose={() => setOpenEditAdditionalFeatureDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Edit Additional Feature</DialogTitle>
        <DialogContent>
          <form>
            <MDTypography variant="body2" sx={{mb: 2}}>
              SubCategory: {editAdditionalFeatureForm.subCategoryName}
            </MDTypography>
            <TextField
              label="Feature Name"
              fullWidth
              margin="normal"
              name="name"
              value={editAdditionalFeatureForm.name}
              onChange={handleAdditionalEditChange}
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              name="description"
              value={editAdditionalFeatureForm.description}
              onChange={handleAdditionalEditChange}
            />
            <TextField
              label="Feature Default Price"
              fullWidth
              margin="normal"
              type="number"
              name="featureDefaultPrice"
              value={editAdditionalFeatureForm.featureDefaultPrice}
              onChange={handleAdditionalEditChange}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editAdditionalFeatureForm.active || false}
                  onChange={handleAdditionalEditChange}
                  name="active"
                />
              }
              label="Active"
            />
            <DialogActions sx={{mt: 2}}>
              <MDButton
                onClick={() => setOpenEditAdditionalFeatureDialog(false)}
                color="secondary"
                variant="outlined">
                Cancel
              </MDButton>
              <MDButton type="submit" color="info" variant="contained">
                Update
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </MDBox>
  );
};

export default React.memo(AdditionalFeatureAssignment);
